import { formatPesoAmount } from '../utils/money';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DateRangePresetPicker from '../components/DateRangePresetPicker';
import InlineLoading from '../components/InlineLoading';
import { listPurchaseOrders } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import {
  PURCHASE_ORDER_STATUS_OPTIONS,
  getPurchaseOrderStatusLabel,
  getPurchaseOrderStatusTone,
} from '../utils/purchaseOrderStatus';

const formatMoney = (value) => `$${formatPesoAmount(Number(value || 0))}`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('es-CL');
};

const PurchaseOrdersListPage = () => {
  const navigate = useNavigate();
  const { branchId: routeBranchId } = useParams();
  const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  const fetchOrders = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listPurchaseOrders(branchId, {
        status: filters.status || undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        pageSize: 0,
      });
      const normalized = normalizePaginatedResponse(data);
      setOrders(normalized.data || []);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'No se pudieron cargar las ordenes.');
    } finally {
      setLoading(false);
    }
  }, [branchId, filters.endDate, filters.search, filters.startDate, filters.status]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-7xl space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ordenes de compra</h1>
            <p className="text-sm text-gray-600">Solicitudes enviadas desde el portal de clientes.</p>
          </div>
          <button
            type="button"
            onClick={fetchOrders}
            className="rounded bg-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-300"
          >
            Refrescar
          </button>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <input
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="Buscar por cliente, RUT o ID"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm lg:max-w-sm"
            />
            <select
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              className="rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Todos los estados</option>
              {PURCHASE_ORDER_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <DateRangePresetPicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onStartDateChange={(startDate) => setFilters((prev) => ({ ...prev, startDate }))}
              onEndDateChange={(endDate) => setFilters((prev) => ({ ...prev, endDate }))}
              containerClassName="min-w-[240px]"
            />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          {loading ? (
            <InlineLoading label="Cargando ordenes" />
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : orders.length === 0 ? (
            <p className="text-sm text-gray-600">No hay ordenes con los filtros seleccionados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Orden</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Cliente</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Estado</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Items</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase text-gray-500">Total</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">Actualizada</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {orders.map((order) => (
                    <tr
                      key={order.id}
                      onClick={() => navigate(`/branches/${branchId}/purchase-orders/${order.id}`)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-3 py-3 font-semibold text-gray-900">#{order.id}</td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-900">{order.Client?.name || 'Cliente'}</div>
                        <div className="text-xs text-gray-500">{order.Client?.rut || ''}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex rounded-full border px-2 py-1 text-xs ${getPurchaseOrderStatusTone(order.status)}`}>
                          {getPurchaseOrderStatusLabel(order.status)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        {order.confirmedItemCount || 0}/{order.itemCount || 0}
                      </td>
                      <td className="px-3 py-3 text-right font-semibold">{formatMoney(order.currentTotalAmount)}</td>
                      <td className="px-3 py-3 text-gray-600">{formatDateTime(order.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOrdersListPage;
